import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  UserCircleIcon,
  ChevronDownIcon,
  DocumentArrowDownIcon,
  WrenchScrewdriverIcon,
  ArrowRightStartOnRectangleIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '../../../contexts/AuthContext'

interface UserMenuProps {
  onLogout: () => void
}

interface MenuItem {
  to: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  isDanger?: boolean
}

/**
 * Componente para el menú desplegable del usuario
 * Diseño minimalista con efectos suaves y código optimizado
 */
const UserMenu: React.FC<UserMenuProps> = ({ onLogout }) => {
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const { user } = useAuth()
  const location = useLocation()

  // Items del menú
  const menuItems: MenuItem[] = useMemo(
    () => [
      { to: '/profile', label: 'Mi Perfil', icon: UserCircleIcon },
      { to: '/tutorials', label: 'Tutoriales', icon: DocumentArrowDownIcon },
      { to: '/settings', label: 'Configuración', icon: WrenchScrewdriverIcon },
    ],
    []
  )

  // Formatear nombre completo
  const formattedName = useMemo(() => {
    if (!user) return 'Usuario'
    const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim()
    return fullName.replace(/\b\w/g, (l) => l.toUpperCase())
  }, [user])

  // Toggle del menú
  const toggleMenu = useCallback(() => {
    setIsOpen((prev) => !prev)
  }, [])

  // Cerrar menú
  const closeMenu = useCallback(() => {
    setIsOpen(false)
  }, [])

  // Manejar logout
  const handleLogout = useCallback(() => {
    closeMenu()
    onLogout()
  }, [closeMenu, onLogout])

  // Cerrar menú al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        closeMenu()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      // Cerrar también con Escape
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') closeMenu()
      }
      document.addEventListener('keydown', handleEscape)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        document.removeEventListener('keydown', handleEscape)
      }
    }
  }, [isOpen, closeMenu])

  // Cerrar menú al cambiar de ruta
  useEffect(() => {
    closeMenu()
  }, [location.pathname, closeMenu])

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={toggleMenu}
        className="group flex items-center gap-2 px-2 py-1 rounded-lg 
                   focus:outline-none
                   transition-all duration-200 hover:bg-gray-50 active:scale-95"
        aria-label="Menú de usuario"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="text-right hidden sm:block max-w-[11rem] md:max-w-[14rem]">
          <div className="text-sm font-medium text-gray-900 transition-colors group-hover:text-gray-700 truncate">
            {formattedName}
          </div>
          <div className="text-xs text-gray-500 truncate">{user?.email || ''}</div>
        </div>
        <div className="relative">
          <UserCircleIcon
            className={`h-8 w-8 transition-all duration-200 ${
              isOpen
                ? 'text-custom-blue'
                : 'text-custom-blue-2 group-hover:text-custom-blue'
            }`}
          />
        </div>
        <ChevronDownIcon
          className={`h-4 w-4 transition-all duration-200 ${
            isOpen
              ? 'text-custom-blue rotate-180'
              : 'text-custom-blue-2 group-hover:text-custom-blue'
          }`}
        />
      </button>

      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-100 
                     z-50 overflow-hidden animate-slide-down"
          role="menu"
          aria-orientation="vertical"
        >
          <div className="border-b border-gray-100 px-4 py-3 sm:hidden">
            <p className="text-sm font-medium text-gray-900 truncate">{formattedName}</p>
            <p className="text-xs text-gray-500 truncate">{user?.email || ''}</p>
          </div>
          <div className="py-1.5">
            {menuItems.map((item) => {
              const Icon = item.icon
              const isActive = location.pathname === item.to
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={closeMenu}
                  className={`group flex items-center px-4 py-2 text-sm transition-all duration-200
                             ${
                               isActive
                                 ? 'bg-custom-blue-50 text-custom-blue font-medium'
                                 : 'text-gray-700 hover:bg-custom-blue-5 hover:text-custom-blue'
                             }`}
                  role="menuitem"
                >
                  <Icon
                    className={`h-4 w-4 mr-3 transition-all duration-200 ${
                      isActive
                        ? 'text-custom-blue'
                        : 'text-gray-400 group-hover:text-custom-blue'
                    }`}
                  />
                  {item.label}
                </Link>
              )
            })}
            <div className="border-t border-gray-100 my-1"></div>
            <button
              onClick={handleLogout}
              className="group flex items-center w-full text-left px-4 py-2 text-sm 
                         text-red-600 hover:bg-red-50 transition-all duration-200"
              role="menuitem"
            >
              <ArrowRightStartOnRectangleIcon className="h-4 w-4 mr-3 text-red-500 group-hover:text-red-600 transition-colors" />
              Cerrar sesión
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default React.memo(UserMenu)

